import { AppStore } from '../state/store';
import { rollAt, shortestAngleDelta } from '../core/track/roll';
import { displayBankDeg } from '../core/track/flyBank';
import { gravityBankDefinable } from '../core/track/orientation';

const PAD_L = 44;
const PAD_R = 10;
const PAD_B_LABELS = 14; // room for distance labels under the plot
const SCROLL_H = 11; // x-axis scrollbar height when zoomed
const PAD_T = 4; // gap under the section band
const BAND_H = 16; // section band strip at the top
const DEG_MIN_DEFAULT = -200;
const DEG_MAX_DEFAULT = 200;
const DEG_RANGE_PAD = 20; // headroom when auto-scaling
const DEG_SPAN_MIN = 40; // never collapse the Y axis tighter than this
const DEG_CLAMP = 720; // manual min/max hard limits
const SCALE_STORAGE_KEY = 'rcsim.rollScale';
const MIN_VIEW_SPAN = 8; // metres — furthest zoom-in
const ZOOM_EPS = 0.5; // treat as fully zoomed-out when within this of L
const BANK_STEP = 1; // degrees per ArrowUp/Down
const BANK_STEP_FAST = 5; // Shift+ArrowUp/Down

interface SimTrace {
  headS: number;
  speedProfile: { s: number; v: number; gVert: number }[] | null;
}

/** Y-axis scale prefs for the banking graph (editor UI — not in the coaster file). */
interface RollScalePrefs {
  auto: boolean;
  minDeg: number;
  maxDeg: number;
}

function loadScalePrefs(): RollScalePrefs {
  try {
    const raw = localStorage.getItem(SCALE_STORAGE_KEY);
    if (!raw) return { auto: false, minDeg: DEG_MIN_DEFAULT, maxDeg: DEG_MAX_DEFAULT };
    const parsed = JSON.parse(raw) as Partial<RollScalePrefs>;
    const minDeg =
      typeof parsed.minDeg === 'number' && isFinite(parsed.minDeg) ? parsed.minDeg : DEG_MIN_DEFAULT;
    const maxDeg =
      typeof parsed.maxDeg === 'number' && isFinite(parsed.maxDeg) ? parsed.maxDeg : DEG_MAX_DEFAULT;
    return normalizeScalePrefs({
      auto: parsed.auto === true,
      minDeg,
      maxDeg,
    });
  } catch {
    return { auto: false, minDeg: DEG_MIN_DEFAULT, maxDeg: DEG_MAX_DEFAULT };
  }
}

function normalizeScalePrefs(prefs: RollScalePrefs): RollScalePrefs {
  let minDeg = Math.max(-DEG_CLAMP, Math.min(DEG_CLAMP, Math.round(prefs.minDeg)));
  let maxDeg = Math.max(-DEG_CLAMP, Math.min(DEG_CLAMP, Math.round(prefs.maxDeg)));
  if (maxDeg - minDeg < DEG_SPAN_MIN) {
    const mid = (minDeg + maxDeg) / 2;
    minDeg = Math.round(mid - DEG_SPAN_MIN / 2);
    maxDeg = minDeg + DEG_SPAN_MIN;
  }
  return { auto: prefs.auto, minDeg, maxDeg };
}

function saveScalePrefs(prefs: RollScalePrefs): void {
  localStorage.setItem(SCALE_STORAGE_KEY, JSON.stringify(prefs));
}

/**
 * Bank-angle-vs-distance graph keyed to track nodes (blue diamonds), plus
 * section bands (station / brake) with draggable ends. Mouse-wheel zooms the
 * x-axis; a scrollbar appears while zoomed in.
 * When the chart is focused: ←/→ or PageUp/PageDown select nodes, ↑/↓ nudge
 * bank (Shift = 5°), Delete clears the node's bank override.
 */
export class RollEditor {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private drag:
    | { kind: 'section'; index: number; edge: 'from' | 'to' }
    | { kind: 'scroll'; grabOff: number }
    | { kind: 'bank'; nodeIndex: number }
    | null = null;
  /** Keyboard / click selection among track nodes (−1 = none). */
  private activeNode = -1;
  /** True while arrow keys nudge bank — one undo step for the whole hold. */
  private bankKeyGesture = false;
  /** Arc-length under the mouse, or null when the pointer is off the graph. */
  private hoverS: number | null = null;
  /** Called when the hover scrub position changes (null = cleared). */
  onHoverS: ((s: number | null) => void) | null = null;
  /** Active vertical axis bounds (manual prefs or last auto-fit). */
  private degMin = DEG_MIN_DEFAULT;
  private degMax = DEG_MAX_DEFAULT;
  private scalePrefs: RollScalePrefs;
  /** Visible x-window: start arc-length and span (metres). */
  private viewStart = 0;
  private viewSpan = 1;
  simTrace: SimTrace = { headS: -1, speedProfile: null };
  private scaleAutoCheck: HTMLInputElement;
  private scaleMinInput: HTMLInputElement;
  private scaleMaxInput: HTMLInputElement;

  constructor(private store: AppStore) {
    this.canvas = document.getElementById('rollcanvas') as HTMLCanvasElement;
    this.ctx = this.canvas.getContext('2d')!;
    this.canvas.tabIndex = 0; // focusable for arrow-key bank editing
    this.scalePrefs = loadScalePrefs();
    this.degMin = this.scalePrefs.minDeg;
    this.degMax = this.scalePrefs.maxDeg;
    const scaleBar = this.mountScaleControls();
    this.scaleAutoCheck = scaleBar.querySelector('input[data-action="auto"]')!;
    this.scaleMaxInput = scaleBar.querySelector('input[data-action="max"]')!;
    this.scaleMinInput = scaleBar.querySelector('input[data-action="min"]')!;
    this.syncScaleControls();
    this.resetView();
    store.on('doc', () => {
      this.activeNode = -1;
      this.resetView();
      this.draw();
    });
    store.on('mode', () => this.draw());
    store.on('selection', () => this.draw());
    store.on('track', () => {
      this.clampActiveNode();
      this.clampView();
      this.draw();
    });
    window.addEventListener('resize', () => this.draw());
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(() => this.draw()).observe(this.canvas);
    }

    this.canvas.addEventListener('pointerdown', (e) => this.onDown(e));
    this.canvas.addEventListener('pointermove', (e) => this.onMove(e));
    this.canvas.addEventListener('pointerup', (e) => this.onUp(e));
    this.canvas.addEventListener('pointerleave', () => {
      if (!this.drag) this.setHoverS(null);
    });
    this.canvas.addEventListener('contextmenu', (e) => this.onContext(e));
    this.canvas.addEventListener('keydown', (e) => this.onKeyDown(e));
    this.canvas.addEventListener('keyup', (e) => this.onKeyUp(e));
    this.canvas.addEventListener('focus', () => this.draw());
    this.canvas.addEventListener('blur', () => {
      this.endBankKeyGesture();
      this.draw();
    });
    this.canvas.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        this.onWheel(e);
      },
      { passive: false },
    );
    this.draw();
  }

  private chartFocused(): boolean {
    return document.activeElement === this.canvas;
  }

  private clampActiveNode(): void {
    const n = this.store.doc.track.nodes.length;
    if (n === 0) this.activeNode = -1;
    else if (this.activeNode >= n) this.activeNode = n - 1;
  }

  private mountScaleControls(): HTMLElement {
    const bar = document.getElementById('bottombar')!;
    const row = document.createElement('div');
    row.id = 'roll-scale-bar';
    row.innerHTML = `<label class="auto"><input type="checkbox" data-action="auto" /> Auto scale</label>
      <label>Max <input type="number" data-action="max" step="10" /></label>
      <label>Min <input type="number" data-action="min" step="10" /></label>`;
    bar.appendChild(row);

    const auto = row.querySelector<HTMLInputElement>('input[data-action="auto"]')!;
    const maxIn = row.querySelector<HTMLInputElement>('input[data-action="max"]')!;
    const minIn = row.querySelector<HTMLInputElement>('input[data-action="min"]')!;

    auto.addEventListener('change', () => {
      this.scalePrefs.auto = auto.checked;
      if (!this.scalePrefs.auto) {
        // Leaving auto: keep the fitted range as the new manual bounds.
        this.scalePrefs = normalizeScalePrefs({
          auto: false,
          minDeg: this.degMin,
          maxDeg: this.degMax,
        });
      }
      saveScalePrefs(this.scalePrefs);
      this.syncScaleControls();
      this.draw();
    });

    const applyManual = () => {
      const maxDeg = parseFloat(maxIn.value);
      const minDeg = parseFloat(minIn.value);
      if (!isFinite(maxDeg) || !isFinite(minDeg)) {
        this.syncScaleControls();
        return;
      }
      this.scalePrefs = normalizeScalePrefs({
        auto: false,
        minDeg,
        maxDeg,
      });
      this.degMin = this.scalePrefs.minDeg;
      this.degMax = this.scalePrefs.maxDeg;
      saveScalePrefs(this.scalePrefs);
      this.syncScaleControls();
      this.draw();
    };

    maxIn.addEventListener('change', applyManual);
    minIn.addEventListener('change', applyManual);
    // Keep typing from also nudging the bank chart when focus is in the inputs.
    for (const el of [auto, maxIn, minIn]) {
      el.addEventListener('keydown', (e) => e.stopPropagation());
      el.addEventListener('keyup', (e) => e.stopPropagation());
    }
    return row;
  }

  private syncScaleControls(): void {
    this.scaleAutoCheck.checked = this.scalePrefs.auto;
    this.scaleMaxInput.value = String(Math.round(this.degMax));
    this.scaleMinInput.value = String(Math.round(this.degMin));
    this.scaleMaxInput.disabled = this.scalePrefs.auto;
    this.scaleMinInput.disabled = this.scalePrefs.auto;
  }

  /** Chart degrees → ground-relative (0° = rails level with the ground). */
  private displayDegAt(s: number, authoredDeg: number): number {
    const frame = this.store.runtime.frameAt(s);
    return displayBankDeg(frame, authoredDeg);
  }

  private trackLength(): number {
    return Math.max(this.store.runtime.totalLength, 1);
  }

  private isZoomed(): boolean {
    return this.viewSpan < this.trackLength() - ZOOM_EPS;
  }

  private resetView(): void {
    this.viewStart = 0;
    this.viewSpan = this.trackLength();
  }

  private clampView(): void {
    const L = this.trackLength();
    this.viewSpan = Math.min(Math.max(this.viewSpan, Math.min(MIN_VIEW_SPAN, L)), L);
    this.viewStart = Math.min(Math.max(this.viewStart, 0), Math.max(0, L - this.viewSpan));
  }

  private dims() {
    const rect = this.canvas.getBoundingClientRect();
    // Buffer size must be integers; comparing to the raw float product re-assigns
    // width on every hover and clears the bitmap.
    const pw = Math.max(1, Math.round(rect.width * devicePixelRatio));
    const ph = Math.max(1, Math.round(rect.height * devicePixelRatio));
    if (this.canvas.width !== pw || this.canvas.height !== ph) {
      this.canvas.width = pw;
      this.canvas.height = ph;
    }
    return { w: rect.width, h: rect.height, pw, ph };
  }

  /** Pointer position in CSS pixels relative to the canvas. */
  private pointerXY(e: MouseEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  private bottomPad(): number {
    return PAD_B_LABELS + (this.isZoomed() ? SCROLL_H : 0);
  }

  private plotWidth(w: number): number {
    return Math.max(w - PAD_L - PAD_R, 1);
  }

  private sToX(s: number, w: number): number {
    return PAD_L + ((s - this.viewStart) / this.viewSpan) * this.plotWidth(w);
  }

  private xToS(x: number, w: number): number {
    const L = this.trackLength();
    const s = this.viewStart + ((x - PAD_L) / this.plotWidth(w)) * this.viewSpan;
    return Math.min(Math.max(s, 0), L);
  }

  private plotTop(): number {
    return BAND_H + PAD_T;
  }

  private plotBottom(h: number): number {
    return h - this.bottomPad();
  }

  private plotHeight(h: number): number {
    return Math.max(this.plotBottom(h) - this.plotTop(), 1);
  }

  /** Fit or apply manual Y bounds for the current draw. */
  private refreshDegRange(): void {
    if (!this.scalePrefs.auto) {
      this.degMin = this.scalePrefs.minDeg;
      this.degMax = this.scalePrefs.maxDeg;
      return;
    }

    let lo = Infinity;
    let hi = -Infinity;
    const note = (deg: number) => {
      if (!isFinite(deg)) return;
      lo = Math.min(lo, deg);
      hi = Math.max(hi, deg);
    };

    const L = this.trackLength();
    const closed = this.store.doc.track.closed;
    const keys = this.store.doc.rollKeys;
    const nodes = this.store.doc.track.nodes;
    const cl = this.store.runtime.centerline;
    const viewEnd = this.viewStart + this.viewSpan;
    const margin = Math.max(this.viewSpan * 0.02, 0.5);

    for (let i = 0; i < nodes.length; i++) {
      const s = cl.spanStartS(i);
      if (!isFinite(s)) continue;
      const dragging = this.drag?.kind === 'bank' && this.drag.nodeIndex === i;
      const selected = this.activeNode === i && this.chartFocused();
      if (!dragging && !selected && (s < this.viewStart - margin || s > viewEnd + margin)) continue;
      note(this.displayDegAt(s, rollAt(keys, s, L, closed)));
    }

    // Dense sample of the visible span — smooth interp can overshoot keys.
    const steps = Math.max(32, Math.min(256, Math.ceil(this.viewSpan * 4)));
    let prevAuthored: number | null = null;
    for (let i = 0; i <= steps; i++) {
      const s = this.viewStart + (this.viewSpan * i) / steps;
      let authored = rollAt(keys, s, L, closed);
      if (prevAuthored !== null) {
        authored = prevAuthored + shortestAngleDelta(prevAuthored, authored);
      }
      prevAuthored = authored;
      note(this.displayDegAt(s, authored));
    }

    if (!isFinite(lo) || !isFinite(hi)) {
      lo = DEG_MIN_DEFAULT;
      hi = DEG_MAX_DEFAULT;
    }

    lo -= DEG_RANGE_PAD;
    hi += DEG_RANGE_PAD;
    if (hi - lo < DEG_SPAN_MIN) {
      const mid = (lo + hi) / 2;
      lo = mid - DEG_SPAN_MIN / 2;
      hi = mid + DEG_SPAN_MIN / 2;
    }

    // Round outward to 10°.
    this.degMin = Math.floor(lo / 10) * 10;
    this.degMax = Math.ceil(hi / 10) * 10;
    if (this.degMax - this.degMin < DEG_SPAN_MIN) {
      this.degMax = this.degMin + DEG_SPAN_MIN;
    }
  }

  private degSpan(): number {
    return Math.max(this.degMax - this.degMin, 1e-6);
  }

  private degToY(deg: number, h: number): number {
    const top = this.plotTop();
    const usable = this.plotHeight(h);
    return top + ((this.degMax - deg) / this.degSpan()) * usable;
  }

  private yToDeg(y: number, h: number): number {
    const top = this.plotTop();
    const usable = this.plotHeight(h);
    return this.degMax - ((y - top) / usable) * this.degSpan();
  }

  /** Nice degree grid step for the current Y span. */
  private degTickStep(): number {
    const span = this.degSpan();
    const target = span / 4;
    const candidates = [15, 30, 45, 60, 90, 120, 180, 360];
    for (const c of candidates) {
      if (c >= target * 0.7) return c;
    }
    return 360;
  }

  private degGridValues(): number[] {
    const step = this.degTickStep();
    const values = new Set<number>([0, this.degMin, this.degMax]);
    const start = Math.ceil(this.degMin / step) * step;
    for (let d = start; d <= this.degMax + 1e-6; d += step) {
      values.add(Math.round(d));
    }
    return [...values].sort((a, b) => b - a);
  }

  /** Nice step for x-axis labels given the visible span. */
  private xTickStep(): number {
    const target = this.viewSpan / 8;
    const pow = Math.pow(10, Math.floor(Math.log10(Math.max(target, 1e-6))));
    const n = target / pow;
    const nice = n < 1.5 ? 1 : n < 3.5 ? 2 : n < 7.5 ? 5 : 10;
    return nice * pow;
  }

  private scrollMetrics(w: number) {
    const L = this.trackLength();
    const trackX0 = PAD_L;
    const trackW = this.plotWidth(w);
    const thumbW = Math.max((this.viewSpan / L) * trackW, 18);
    const travel = Math.max(trackW - thumbW, 0);
    const thumbX = trackX0 + (travel > 0 ? (this.viewStart / Math.max(L - this.viewSpan, 1e-9)) * travel : 0);
    return { trackX0, trackW, thumbX, thumbW, travel };
  }

  private scrollY(h: number): number {
    return h - SCROLL_H;
  }

  draw(): void {
    this.clampView();
    const { w, h, pw, ph } = this.dims();
    this.refreshDegRange();
    if (this.scalePrefs.auto) {
      const maxStr = String(Math.round(this.degMax));
      const minStr = String(Math.round(this.degMin));
      if (this.scaleMaxInput.value !== maxStr || this.scaleMinInput.value !== minStr) {
        this.syncScaleControls();
      }
    }
    const ctx = this.ctx;
    ctx.save();
    // Scale by actual buffer/CSS ratio so drawing lines up with pointer coords
    // even when round(rect * dpr) ≠ rect * dpr.
    ctx.scale(pw / Math.max(w, 1e-6), ph / Math.max(h, 1e-6));
    ctx.clearRect(0, 0, w, h);
    const L = this.trackLength();
    const closed = this.store.doc.track.closed;
    const plotTop = this.plotTop();
    const plotBottom = this.plotBottom(h);
    const viewEnd = this.viewStart + this.viewSpan;
    const zoomed = this.isZoomed();

    // Horizontal degree grid (behind curves)
    ctx.strokeStyle = '#2a323c';
    ctx.fillStyle = '#75828f';
    ctx.font = '10px sans-serif';
    for (const deg of this.degGridValues()) {
      const y = this.degToY(deg, h);
      if (y < plotTop - 1 || y > plotBottom + 1) continue;
      ctx.beginPath();
      ctx.moveTo(PAD_L, y);
      ctx.lineTo(w - PAD_R, y);
      ctx.stroke();
      ctx.fillText(`${deg}°`, 6, y + 3);
    }

    // Clip plot + section band to the x plot area (keep y-labels outside)
    ctx.save();
    ctx.beginPath();
    ctx.rect(PAD_L, 0, this.plotWidth(w), plotBottom);
    ctx.clip();

    // section bands
    const bandColors: Record<string, string> = { station: '#2b6b75', brake: '#7c352f' };
    ctx.fillStyle = '#20262d';
    ctx.fillRect(PAD_L, 0, this.plotWidth(w), BAND_H);
    this.store.doc.sections.forEach((sec) => {
      ctx.fillStyle = bandColors[sec.kind];
      const x0 = this.sToX(sec.from, w);
      const x1 = this.sToX(sec.to >= sec.from ? sec.to : L, w);
      ctx.fillRect(x0, 1, Math.max(x1 - x0, 2), BAND_H - 2);
      if (sec.to < sec.from) {
        const xw = this.sToX(sec.to, w);
        ctx.fillRect(this.sToX(0, w), 1, xw - this.sToX(0, w), BAND_H - 2);
      }
      if (sec.from >= this.viewStart - 1 && sec.from <= viewEnd + 1) {
        ctx.fillStyle = '#dde3ea';
        ctx.font = '9px sans-serif';
        ctx.fillText(sec.kind, x0 + 3, BAND_H - 5);
      }
    });

    // vertical grid
    ctx.fillStyle = '#75828f';
    ctx.font = '10px sans-serif';
    const xStep = this.xTickStep();
    const s0 = Math.ceil(this.viewStart / xStep) * xStep;
    for (let s = s0; s <= viewEnd + 1e-6; s += xStep) {
      const x = this.sToX(s, w);
      ctx.strokeStyle = '#232a32';
      ctx.beginPath();
      ctx.moveTo(x, plotTop);
      ctx.lineTo(x, plotBottom);
      ctx.stroke();
    }

    // elevation profile (context) — stays inside the plot band
    let maxY = 1;
    for (const f of this.store.runtime.frames) maxY = Math.max(maxY, f.pos.y);
    const elevSpan = this.plotHeight(h) - 8;
    ctx.strokeStyle = '#3d4a57';
    ctx.beginPath();
    let elevStarted = false;
    for (let i = 0; i < this.store.runtime.frames.length; i++) {
      const f = this.store.runtime.frames[i];
      if (f.s < this.viewStart - 1 || f.s > viewEnd + 1) {
        elevStarted = false;
        continue;
      }
      const x = this.sToX(f.s, w);
      const y = plotBottom - 4 - (f.pos.y / maxY) * elevSpan;
      if (!elevStarted) {
        ctx.moveTo(x, y);
        elevStarted = true;
      } else ctx.lineTo(x, y);
    }
    ctx.stroke();

    // Track nodes — vertical ticks; markers are drawn on the bank curve (merged with keys)
    const nodes = this.store.doc.track.nodes;
    const cl = this.store.runtime.centerline;
    const sel = this.store.selection;
    for (let i = 0; i < nodes.length; i++) {
      const s = cl.spanStartS(i);
      if (!isFinite(s) || s < this.viewStart - 1 || s > viewEnd + 1) continue;
      const x = this.sToX(s, w);
      const selected = sel.has(nodes[i].id);
      ctx.strokeStyle = selected ? '#ffa726' : '#3a4450';
      ctx.lineWidth = selected ? 1.5 : 1;
      ctx.beginPath();
      ctx.moveTo(x, plotTop);
      ctx.lineTo(x, plotBottom);
      ctx.stroke();
      ctx.lineWidth = 1;
    }

    // sim traces: speed (green) and vertical G (orange)
    const prof = this.simTrace.speedProfile;
    if (prof && prof.length > 1) {
      let vMax = 1;
      for (const p of prof) vMax = Math.max(vMax, p.v);
      ctx.strokeStyle = '#58c470';
      ctx.beginPath();
      let started = false;
      for (const p of prof) {
        if (p.s < this.viewStart - 1 || p.s > viewEnd + 1) {
          started = false;
          continue;
        }
        const x = this.sToX(p.s, w);
        const y = plotBottom - 4 - (p.v / vMax) * elevSpan;
        if (!started) {
          ctx.moveTo(x, y);
          started = true;
        } else ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.strokeStyle = '#e0a028';
      ctx.beginPath();
      started = false;
      for (const p of prof) {
        if (p.s < this.viewStart - 1 || p.s > viewEnd + 1) {
          started = false;
          continue;
        }
        const g = Math.min(Math.max(p.gVert, -2), 6);
        const x = this.sToX(p.s, w);
        const y = this.degToY(g * 30, h); // 1 G = 30° of graph height
        if (!started) {
          ctx.moveTo(x, y);
          started = true;
        } else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }

    // roll curve — sample across the visible window
    // Closed tracks: unwrap consecutive authored samples so −360→0 at the seam
    // draws flat.
    ctx.strokeStyle = '#5ba7f0';
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    const keys = this.store.doc.rollKeys;
    const steps = Math.max(Math.floor(this.plotWidth(w)), 10);
    let prevAuthored: number | null = null;
    for (let i = 0; i <= steps; i++) {
      const s = this.viewStart + (this.viewSpan * i) / steps;
      let authored = rollAt(keys, s, L, closed);
      if (prevAuthored !== null) {
        authored = prevAuthored + shortestAngleDelta(prevAuthored, authored);
      }
      prevAuthored = authored;
      const deg = this.displayDegAt(s, authored);
      const x = this.sToX(s, w);
      const y = this.degToY(deg, h);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.lineWidth = 1;

    // Track-node bank handles on the curve (legacy free roll-key dots removed)
    const hiliteNode = this.hiliteNodeIndex();
    for (let i = 0; i < nodes.length; i++) {
      const s = cl.spanStartS(i);
      if (!isFinite(s) || s < this.viewStart - 1 || s > viewEnd + 1) continue;
      const authored = rollAt(keys, s, L, closed);
      const disp = this.displayDegAt(s, authored);
      const x = this.sToX(s, w);
      const y = this.degToY(disp, h);
      const selected = sel.has(nodes[i].id) || i === hiliteNode;
      ctx.fillStyle = selected ? '#ffa726' : '#5ba7f0';
      ctx.beginPath();
      ctx.moveTo(x, y - 6);
      ctx.lineTo(x + 5, y);
      ctx.lineTo(x, y + 6);
      ctx.lineTo(x - 5, y);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#1d2530';
      ctx.stroke();
    }

    // tooltip while dragging or keyboard-editing a node's bank
    if (hiliteNode >= 0) {
      const s = cl.spanStartS(hiliteNode);
      if (isFinite(s) && s >= this.viewStart - 1 && s <= viewEnd + 1) {
        const authored = rollAt(keys, s, L, closed);
        const disp = this.displayDegAt(s, authored);
        this.drawBankTooltip(ctx, this.sToX(s, w), this.degToY(disp, h), disp, w, plotTop);
      }
    }

    // train position marker
    if (this.simTrace.headS >= 0) {
      if (this.simTrace.headS >= this.viewStart && this.simTrace.headS <= viewEnd) {
        const x = this.sToX(this.simTrace.headS, w);
        ctx.strokeStyle = '#ef5350';
        ctx.beginPath();
        ctx.moveTo(x, plotTop);
        ctx.lineTo(x, plotBottom);
        ctx.stroke();
      }
    }

    // hover scrub — matches the 3D track highlight
    if (this.hoverS !== null && this.hoverS >= this.viewStart && this.hoverS <= viewEnd) {
      const x = this.sToX(this.hoverS, w);
      ctx.strokeStyle = '#ffd54f';
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(x, plotTop);
      ctx.lineTo(x, plotBottom);
      ctx.stroke();
      ctx.lineWidth = 1;
      const deg = this.displayDegAt(this.hoverS, rollAt(keys, this.hoverS, L, closed));
      ctx.fillStyle = '#ffd54f';
      ctx.font = '10px sans-serif';
      ctx.fillText(`${this.hoverS.toFixed(0)} m · ${deg.toFixed(0)}°`, Math.min(x + 6, w - 90), plotTop + 10);
    }

    ctx.restore(); // end plot clip

    // distance labels under the plot
    ctx.fillStyle = '#75828f';
    ctx.font = '10px sans-serif';
    const labelY = plotBottom + 11;
    for (let s = s0; s <= viewEnd + 1e-6; s += xStep) {
      const x = this.sToX(s, w);
      if (x < PAD_L - 2 || x > w - PAD_R + 2) continue;
      ctx.fillText(`${Math.round(s)}`, x - 8, labelY);
    }

    if (prof && prof.length > 1) {
      ctx.fillStyle = '#58c470';
      ctx.fillText('speed', w - 76, plotTop + 10);
      ctx.fillStyle = '#e0a028';
      ctx.fillText('vert G', w - 40, plotTop + 10);
    }

    // x-axis scrollbar while zoomed in
    if (zoomed) {
      const sy = this.scrollY(h);
      const { trackX0, trackW, thumbX, thumbW } = this.scrollMetrics(w);
      ctx.fillStyle = '#161a20';
      ctx.fillRect(trackX0, sy, trackW, SCROLL_H);
      ctx.strokeStyle = '#2c333c';
      ctx.strokeRect(trackX0 + 0.5, sy + 0.5, trackW - 1, SCROLL_H - 1);
      ctx.fillStyle = this.drag?.kind === 'scroll' ? '#6a7a8c' : '#4a5562';
      ctx.beginPath();
      const r = 3;
      const tx = thumbX;
      const ty = sy + 2;
      const tw = thumbW;
      const th = SCROLL_H - 4;
      ctx.moveTo(tx + r, ty);
      ctx.arcTo(tx + tw, ty, tx + tw, ty + th, r);
      ctx.arcTo(tx + tw, ty + th, tx, ty + th, r);
      ctx.arcTo(tx, ty + th, tx, ty, r);
      ctx.arcTo(tx, ty, tx + tw, ty, r);
      ctx.closePath();
      ctx.fill();
    }

    ctx.restore();
  }

  /** Floating label next to a dragged bank key. */
  private drawBankTooltip(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    deg: number,
    w: number,
    plotTop: number,
  ): void {
    this.drawGraphTooltip(ctx, x, y, `${deg}°`, '#ffd54f', w, plotTop);
  }

  private drawGraphTooltip(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    label: string,
    accent: string,
    w: number,
    plotTop: number,
  ): void {
    ctx.font = '11px sans-serif';
    const tw = ctx.measureText(label).width;
    const padX = 6;
    const padY = 4;
    const bw = tw + padX * 2;
    const bh = 16;
    let bx = x + 10;
    if (bx + bw > w - PAD_R) bx = x - 10 - bw;
    let by = y - bh - 8;
    if (by < plotTop) by = y + 8;
    ctx.fillStyle = 'rgba(29, 37, 48, 0.92)';
    ctx.strokeStyle = accent;
    ctx.lineWidth = 1;
    ctx.beginPath();
    const r = 3;
    ctx.moveTo(bx + r, by);
    ctx.arcTo(bx + bw, by, bx + bw, by + bh, r);
    ctx.arcTo(bx + bw, by + bh, bx, by + bh, r);
    ctx.arcTo(bx, by + bh, bx, by, r);
    ctx.arcTo(bx, by, bx + bw, by, r);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = accent;
    ctx.fillText(label, bx + padX, by + bh - padY - 1);
  }

  private setHoverS(s: number | null): void {
    if (this.hoverS === s) return;
    // Avoid redraw spam when s is nearly unchanged (sub-pixel moves).
    if (s !== null && this.hoverS !== null && Math.abs(s - this.hoverS) < 0.05) return;
    this.hoverS = s;
    this.onHoverS?.(s);
    this.draw();
  }

  private setViewStartFromThumb(thumbX: number, w: number): void {
    const L = this.trackLength();
    const { trackX0, travel } = this.scrollMetrics(w);
    if (travel <= 0) {
      this.viewStart = 0;
      return;
    }
    const t = Math.min(Math.max((thumbX - trackX0) / travel, 0), 1);
    this.viewStart = t * (L - this.viewSpan);
    this.clampView();
  }

  // ── interaction ──────────────────────────────────────────────────────────

  /** Track-node index whose bank-curve marker is under (x,y), or -1. */
  private hitNodeMarker(x: number, y: number, w: number, h: number): number {
    const nodes = this.store.doc.track.nodes;
    const cl = this.store.runtime.centerline;
    const keys = this.store.doc.rollKeys;
    const L = this.trackLength();
    const closed = this.store.doc.track.closed;
    for (let i = 0; i < nodes.length; i++) {
      const s = cl.spanStartS(i);
      if (!isFinite(s)) continue;
      const authored = rollAt(keys, s, L, closed);
      const disp = this.displayDegAt(s, authored);
      const nx = this.sToX(s, w);
      const ny = this.degToY(disp, h);
      if (Math.hypot(nx - x, ny - y) < 9) return i;
    }
    return -1;
  }

  /** Nearest track node by screen-x. */
  private nearestNodeByX(x: number, w: number, maxDist = 28): number {
    const nodes = this.store.doc.track.nodes;
    const cl = this.store.runtime.centerline;
    let best = -1;
    let bestD = maxDist;
    for (let i = 0; i < nodes.length; i++) {
      const s = cl.spanStartS(i);
      if (!isFinite(s)) continue;
      const d = Math.abs(this.sToX(s, w) - x);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best;
  }

  /** Arc length of a track node, or NaN. */
  private nodeS(nodeIndex: number): number {
    return this.store.runtime.centerline.spanStartS(nodeIndex);
  }

  /**
   * Pin bank at a track node from mouse Y. Caller must have begun a gesture.
   */
  private applyBankAtNode(nodeIndex: number, y: number, h: number): void {
    const node = this.store.doc.track.nodes[nodeIndex];
    const s = this.nodeS(nodeIndex);
    if (!node || !isFinite(s)) return;
    const frame = this.store.runtime.frameAt(s);
    if (!gravityBankDefinable(frame.tan)) return;
    const display = Math.round(this.yToDeg(y, h));
    node.forcedBankDeg = display;
    this.store.updateGesture([nodeIndex]);
  }

  /** Select a single track node in the 3D editor (chart ↔ viewport sync). */
  private selectTrackNode(nodeIndex: number): void {
    const node = this.store.doc.track.nodes[nodeIndex];
    if (!node) return;
    this.activeNode = nodeIndex;
    this.store.setSelection([node.id]);
  }

  private hitSectionEdge(x: number, y: number, w: number): { index: number; edge: 'from' | 'to' } | null {
    if (y > BAND_H + 2) return null;
    const sections = this.store.doc.sections;
    for (let i = 0; i < sections.length; i++) {
      if (Math.abs(this.sToX(sections[i].from, w) - x) < 6) return { index: i, edge: 'from' };
      if (Math.abs(this.sToX(sections[i].to, w) - x) < 6) return { index: i, edge: 'to' };
    }
    return null;
  }

  private hitScroll(x: number, y: number, w: number, h: number): 'thumb' | 'track' | null {
    if (!this.isZoomed()) return null;
    const sy = this.scrollY(h);
    if (y < sy || y > h) return null;
    const { trackX0, trackW, thumbX, thumbW } = this.scrollMetrics(w);
    if (x < trackX0 || x > trackX0 + trackW) return null;
    if (x >= thumbX && x <= thumbX + thumbW) return 'thumb';
    return 'track';
  }

  private onWheel(e: WheelEvent): void {
    const { w } = this.dims();
    const { x } = this.pointerXY(e);
    const L = this.trackLength();

    // Normalize wheel deltas (pixels / lines / pages)
    let dx = e.deltaX;
    let dy = e.deltaY;
    if (e.deltaMode === 1) {
      dx *= 16;
      dy *= 16;
    } else if (e.deltaMode === 2) {
      dx *= this.plotWidth(w);
      dy *= this.plotWidth(w);
    }

    // Horizontal scroll wheel → pan the x-axis when zoomed in
    if (Math.abs(dx) > Math.abs(dy) && this.isZoomed()) {
      this.viewStart += (dx / this.plotWidth(w)) * this.viewSpan;
      this.clampView();
      this.draw();
      return;
    }

    if (dy === 0) return;

    // Vertical wheel → zoom toward cursor (or view centre)
    const focusS =
      x >= PAD_L && x <= w - PAD_R ? this.xToS(x, w) : this.viewStart + this.viewSpan * 0.5;
    const t = (focusS - this.viewStart) / this.viewSpan;
    const factor = dy > 0 ? 1.18 : 1 / 1.18;
    this.viewSpan = Math.min(Math.max(this.viewSpan * factor, Math.min(MIN_VIEW_SPAN, L)), L);
    this.viewStart = focusS - t * this.viewSpan;
    this.clampView();
    this.draw();
  }

  private onDown(e: PointerEvent): void {
    this.canvas.focus();
    const { w, h } = this.dims();
    const { x, y } = this.pointerXY(e);

    // Right button: scrub bank of the nearest track node (slide to paint).
    if (e.button === 2) {
      if (y <= BAND_H || (this.isZoomed() && y >= this.scrollY(h))) return;
      let ni = this.hitNodeMarker(x, y, w, h);
      if (ni < 0) ni = this.nearestNodeByX(x, w);
      if (ni < 0) return;
      this.selectTrackNode(ni);
      this.store.beginGesture();
      this.drag = { kind: 'bank', nodeIndex: ni };
      this.canvas.setPointerCapture(e.pointerId);
      this.applyBankAtNode(ni, y, h);
      this.setHoverS(this.nodeS(ni));
      this.draw();
      return;
    }

    if (e.button !== 0) return;

    // Ctrl/Cmd+click clears bank override at a track node
    if (e.ctrlKey || e.metaKey) {
      let ni = this.hitNodeMarker(x, y, w, h);
      if (ni < 0) ni = this.nearestNodeByX(x, w);
      if (ni >= 0) {
        this.store.commit((d) => {
          delete d.track.nodes[ni].forcedBankDeg;
        }, [ni]);
        this.selectTrackNode(ni);
      }
      this.draw();
      return;
    }

    const scrollHit = this.hitScroll(x, y, w, h);
    if (scrollHit) {
      const { thumbW } = this.scrollMetrics(w);
      if (scrollHit === 'track') {
        this.setViewStartFromThumb(x - thumbW * 0.5, w);
      }
      const m = this.scrollMetrics(w);
      this.drag = { kind: 'scroll', grabOff: x - m.thumbX };
      this.canvas.setPointerCapture(e.pointerId);
      this.draw();
      return;
    }

    const sec = this.hitSectionEdge(x, y, w);
    if (sec) {
      this.drag = { kind: 'section', ...sec };
      this.store.beginGesture();
      this.canvas.setPointerCapture(e.pointerId);
      return;
    }

    // Track-node marker: select and scrub bank (s stays pinned to the node)
    const ni = this.hitNodeMarker(x, y, w, h);
    if (ni >= 0) {
      this.selectTrackNode(ni);
      this.store.beginGesture();
      this.drag = { kind: 'bank', nodeIndex: ni };
      this.canvas.setPointerCapture(e.pointerId);
      this.applyBankAtNode(ni, y, h);
      this.draw();
      return;
    }

    this.draw();
  }

  private onMove(e: PointerEvent): void {
    const beforeW = this.canvas.width;
    const beforeH = this.canvas.height;
    const { w, h } = this.dims();
    const { x, y } = this.pointerXY(e);

    if (!this.drag) {
      if (this.canvas.width !== beforeW || this.canvas.height !== beforeH) this.draw();
      const overScroll = this.hitScroll(x, y, w, h) !== null;
      const overHandle =
        this.hitNodeMarker(x, y, w, h) >= 0 || this.hitSectionEdge(x, y, w) !== null;
      this.canvas.style.cursor = overScroll ? 'default' : overHandle ? 'grab' : 'crosshair';
      if (x >= PAD_L && x <= w - PAD_R && y < this.scrollY(h)) this.setHoverS(this.xToS(x, w));
      else this.setHoverS(null);
      return;
    }
    if (this.drag.kind === 'scroll') {
      this.setViewStartFromThumb(x - this.drag.grabOff, w);
      this.draw();
      return;
    }
    if (this.drag.kind === 'bank') {
      // Prefer the node under the cursor so sliding paints along the track;
      // otherwise keep scrubbing the current node (vertical-only adjust).
      const near = this.nearestNodeByX(x, w);
      const ni = near >= 0 ? near : this.drag.nodeIndex;
      this.drag.nodeIndex = ni;
      this.selectTrackNode(ni);
      this.applyBankAtNode(ni, y, h);
      this.setHoverS(this.nodeS(ni));
      return;
    }
    // section edge
    const sec = this.store.doc.sections[this.drag.index];
    sec[this.drag.edge] = this.xToS(x, w);
    this.store.updateGesture();
    this.setHoverS(this.xToS(x, w));
  }

  private onUp(e: PointerEvent): void {
    if (this.drag) {
      if (this.drag.kind !== 'scroll') this.store.endGesture();
      this.drag = null;
      this.canvas.releasePointerCapture(e.pointerId);
      this.draw();
    }
    const rect = this.canvas.getBoundingClientRect();
    const inside =
      e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
    if (!inside) this.setHoverS(null);
  }

  private onContext(e: MouseEvent): void {
    e.preventDefault(); // RMB is bank scrub — don't show the browser menu
  }

  // ── keyboard (chart focused) ──────────────────────────────────────────────

  /** Node currently highlighted: drag target, else keyboard selection while focused. */
  private hiliteNodeIndex(): number {
    if (this.drag?.kind === 'bank') return this.drag.nodeIndex;
    if (this.chartFocused() && this.activeNode >= 0) return this.activeNode;
    return -1;
  }

  private endBankKeyGesture(): void {
    if (!this.bankKeyGesture) return;
    this.bankKeyGesture = false;
    this.store.endGesture();
  }

  private onKeyDown(e: KeyboardEvent): void {
    if (!this.chartFocused()) return;
    if (
      e.key !== 'ArrowLeft' &&
      e.key !== 'ArrowRight' &&
      e.key !== 'ArrowUp' &&
      e.key !== 'ArrowDown' &&
      e.key !== 'PageUp' &&
      e.key !== 'PageDown' &&
      e.key !== 'Delete' &&
      e.key !== 'Backspace'
    )
      return;
    e.preventDefault();
    e.stopPropagation();

    if (e.key === 'Delete' || e.key === 'Backspace') {
      this.endBankKeyGesture();
      this.clearActiveNodeBank();
      return;
    }

    if (
      e.key === 'PageUp' ||
      e.key === 'PageDown' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight'
    ) {
      this.endBankKeyGesture();
      const dir: 1 | -1 =
        e.key === 'PageDown' || e.key === 'ArrowRight' ? 1 : -1;
      this.stepActiveNode(dir);
      return;
    }

    const step = e.shiftKey ? BANK_STEP_FAST : BANK_STEP;
    const delta = e.key === 'ArrowUp' ? step : -step;
    this.nudgeActiveBank(delta);
  }

  private onKeyUp(e: KeyboardEvent): void {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      this.endBankKeyGesture();
    }
  }

  /** Clear the bank override on the keyboard-selected track node. */
  private clearActiveNodeBank(): void {
    if (this.activeNode < 0) return;
    this.store.commit((d) => {
      delete d.track.nodes[this.activeNode].forcedBankDeg;
    }, [this.activeNode]);
    this.draw();
  }

  /** Move selection to the previous/next track node and keep it in view. */
  private stepActiveNode(dir: 1 | -1): void {
    const n = this.store.doc.track.nodes.length;
    if (n === 0) return;
    if (this.activeNode < 0) {
      const targetS = this.hoverS ?? this.viewStart + this.viewSpan * 0.5;
      const cl = this.store.runtime.centerline;
      let best = 0;
      let bestD = Infinity;
      for (let i = 0; i < n; i++) {
        const s = cl.spanStartS(i);
        if (!isFinite(s)) continue;
        const d = Math.abs(s - targetS);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      }
      this.selectTrackNode(best);
    } else {
      this.selectTrackNode((this.activeNode + dir + n) % n);
    }
    this.ensureNodeVisible(this.activeNode);
    this.draw();
  }

  /** Nudge the active node's bank by `delta` degrees (one undo gesture per hold). */
  private nudgeActiveBank(delta: number): void {
    if (this.activeNode < 0) {
      this.stepActiveNode(1);
      if (this.activeNode < 0) return;
    }
    const node = this.store.doc.track.nodes[this.activeNode];
    const s = this.nodeS(this.activeNode);
    if (!node || !isFinite(s)) return;
    const frame = this.store.runtime.frameAt(s);
    if (!gravityBankDefinable(frame.tan)) return;
    if (!this.bankKeyGesture) {
      this.store.beginGesture();
      this.bankKeyGesture = true;
    }
    const L = this.trackLength();
    const closed = this.store.doc.track.closed;
    const keys = this.store.doc.rollKeys;
    const current = node.forcedBankDeg ?? this.displayDegAt(s, rollAt(keys, s, L, closed));
    node.forcedBankDeg = Math.round(current + delta);
    this.store.updateGesture([this.activeNode]);
    this.setHoverS(s);
    this.draw();
  }

  /** Pan the x-window so node `index` stays comfortably inside. */
  private ensureNodeVisible(index: number): void {
    const s = this.nodeS(index);
    if (!isFinite(s)) return;
    const margin = Math.min(this.viewSpan * 0.15, 8);
    if (s < this.viewStart + margin) {
      this.viewStart = s - margin;
    } else if (s > this.viewStart + this.viewSpan - margin) {
      this.viewStart = s - this.viewSpan + margin;
    }
    this.clampView();
    this.setHoverS(s);
  }
}
